import {
  Box,
  Button,
  ButtonProps,
  Divider,
  Grid,
  Popover,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { FC, useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { useGet } from '../../hooks/get'
import { useUser } from '../../hooks/user'
import { SessionService } from '../../services/session'
import { themeMeta } from '../../theme/meta'
import { Fa } from '../Fa'
import { UserCard } from '../UserCard'

const MySchedule: FC = () => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const { signOut } = useUser()

  const [scheduleEntry, setScheduleEntry] = useState<HTMLButtonElement>()
  const [pendingBookingId, setPendingBookingId] = useState<number>()

  const {
    data,
    isValidating: loading,
    error: dataError,
    mutate: mutateData,
  } = useGet<Record<string, Dto.Booking[]>>(
    scheduleEntry && 'member_portal/schedule',
    { memberAuth: true, cache: false }
  )

  const createHandleCancelClick = (booking: Dto.Booking) => async () => {
    try {
      const cancelled = await SessionService.cancelBooking(
        booking,
        booking.session,
        {
          onBeforeRequest: () => {
            setPendingBookingId(booking.id)
          },
        }
      )

      if (!cancelled) {
        return
      }

      await mutateData()
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      }
    } finally {
      setPendingBookingId(undefined)
    }
  }

  const handleScheduleEntryClick = useCallback<
    Required<ButtonProps>['onClick']
  >((e) => {
    setScheduleEntry(e.currentTarget)
  }, [])

  const handleScheduleClose = useCallback(() => {
    setScheduleEntry(undefined)
  }, [])

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<Fa name="chevron-down" />}
        onClick={handleScheduleEntryClick}
      >
        {t('My schedule')}
      </Button>
      <Popover
        open={!!scheduleEntry}
        anchorEl={scheduleEntry}
        onClose={handleScheduleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              inlineSize: 500,
              border: `2px solid ${theme.palette.divider}`,
              borderRadius: theme.spacing(1),
              ...(downSm && {
                boxShadow: `0 0 ${theme.spacing(2)} rgba(0, 0, 0, 0.08)`,
              }),
            },
          },
        }}
      >
        {loading ? (
          <Box component="ol">
            <Box component="li" pt={2}>
              <Typography variant="h5" px={{ xs: 2, sm: 3 }}>
                <Skeleton width="2em" />
              </Typography>
              <Box component="ol">
                <Grid
                  component="li"
                  container
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'center' }}
                  spacing={1}
                  p={{ sm: 1 }}
                  pt={{ xs: 1 }}
                  pr={{ sm: 3 }}
                  pb={{ xs: 3 }}
                  borderBottom={1}
                  borderColor={theme.palette.divider}
                >
                  <Grid container size="grow">
                    <Grid>
                      <Box minWidth={60} textAlign="center">
                        <Skeleton
                          variant="rounded"
                          width="2em"
                          sx={{ height: '3em', mx: 'auto' }}
                        />
                      </Box>
                    </Grid>
                    <Grid size="grow">
                      <Stack spacing={0.5}>
                        <Typography>
                          <Skeleton width="4em" />
                        </Typography>
                        <Typography variant="h4">
                          <Skeleton width="90%" />
                        </Typography>
                        <Typography variant="body2">
                          <Skeleton width="50%" />
                        </Typography>
                        <Typography variant="body2">
                          <Skeleton width="40%" />
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                  <Grid px={{ xs: 2, sm: 0 }}>
                    <Skeleton variant="rounded" width="100%">
                      <Button variant="outlined" size="small">
                        {t('Cancel booking')}
                      </Button>
                    </Skeleton>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        ) : dataError ? (
          renderScheduleMessage([
            t('Unable to load your schedule.'),
            t('Please check try again later.'),
          ])
        ) : (
          data &&
          (Object.keys(data).length ? (
            <Box component="ol" maxHeight={540} overflow="auto">
              {Object.entries(data).map(([group, bookings]) => (
                <Box key={group} component="li" pt={2}>
                  <Typography
                    variant="h5"
                    px={{ xs: 2, sm: 3 }}
                    color="textSecondary"
                  >
                    {group}
                  </Typography>
                  <Box component="ol">
                    {bookings.map((booking) => {
                      const { session } = booking

                      if (!session) {
                        return
                      }

                      const startTime = dayjs(
                        session.session_start_time_utc
                      ).tz()
                      const endTime = dayjs(session.session_end_time_utc).tz()
                      return (
                        <Grid
                          key={booking.id}
                          component="li"
                          container
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ sm: 'center' }}
                          spacing={1}
                          p={{ sm: 1 }}
                          pt={{ xs: 1 }}
                          pr={{ sm: 3 }}
                          pb={{ xs: 3 }}
                          borderBottom={1}
                          borderColor={theme.palette.divider}
                          sx={{
                            '&:hover': {
                              background: themeMeta.colors.monochrome.bg,
                            },
                          }}
                        >
                          <Grid container size="grow">
                            <Grid>
                              <Box minWidth={60} textAlign="center">
                                <Typography>
                                  {startTime.format('ddd')}
                                </Typography>
                                <Typography variant="h2">
                                  {startTime.format('D')}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid>
                              <Stack spacing={0.5}>
                                <Typography>
                                  {[startTime, endTime]
                                    .map((time) => time.format('HH:mm'))
                                    .join(' ~ ')}
                                </Typography>
                                <Typography variant="h4">
                                  {session.gym_category.category.name}
                                </Typography>
                                <UserCard user={session.gym_staff.user} />
                                {session.gym_room && (
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {session.gym_room.name}
                                  </Typography>
                                )}
                              </Stack>
                            </Grid>
                          </Grid>
                          <Grid px={{ xs: 2, sm: 0 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              loading={pendingBookingId === booking.id}
                              onClick={createHandleCancelClick(booking)}
                              {...(downSm && { fullWidth: true })}
                            >
                              {booking.is_confirmed
                                ? t('Cancel booking')
                                : t('Cancel waitlist')}
                            </Button>
                          </Grid>
                        </Grid>
                      )
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            renderScheduleMessage([
              t('Nothing on your schedule yet.'),
              t(
                'Once you make a booking or join a waitlist, it will show up here.'
              ),
            ])
          ))
        )}
        <Divider sx={{ borderBottomWidth: 2, marginBlockStart: '-1px' }} />
        <Button
          variant="text"
          color="inherit"
          startIcon={<Fa name="arrow-right-from-bracket" />}
          onClick={signOut}
          sx={{
            inlineSize: '100%',
            justifyContent: 'initial',
            borderRadius: 0,
          }}
        >
          {t('Sign out')}
        </Button>
      </Popover>
    </>
  )
}

const renderScheduleMessage = (lines: string[]) => (
  <Box p={4} textAlign="center">
    {lines.map((line) => (
      <Typography key={line} color={themeMeta.colors.monochrome.label}>
        {line}
      </Typography>
    ))}
  </Box>
)

export { MySchedule }
